import { useEffect, useState } from 'react';
import type { GetStaticProps, NextPage } from 'next';
import { getWindow, Stack } from '@stitch-fix/mode-react';
import { G, S } from '@mobily/ts-belt';
import Head from 'next/head';
import { Layout } from '@stitch-fix/sf-next';
import { useScreenViewEvent } from '@stitch-fix/event-reporter';
import { PagePlaceholder } from '../../components/PagePlaceholder/PagePlaceholder';
import ResultsLayout from '../../components/ResultsLayout';
import FixPromotion from '../../components/FixPromotion';
import {
  UseQueryParamReturn,
  useSearchParams,
} from '../../utils/hooks/useSearchParams';
import {
  GetClientInfoQuery,
  useGetClientInfoQuery,
} from '../../utils/hooks/getClientInfo';
import { ResultsStateMachineProvider } from '../../utils/machines/results/context-provider';
import { ToastProvider } from '../../utils/contexts/toast-provider';
import { COPY } from '../../utils/copy';
import { getCopy } from '../../utils/getCopy';
import { CopyFragment } from '../../utils/hooks/getCopy';
import { CopyProvider } from '../../utils/contexts/copy-provider';
import { useRedirect } from '../../utils/hooks/useRedirect';

/**
 * this is a potentially fragile but "ok for now" solution to easily
 * let `categories-next-ui` talk to Knit.
 */
declare global {
  interface StitchfixGlobal {
    onSearch?: (str: string) => void;
  }
}

interface PageProps {
  copy: CopyFragment;
}

export const getStaticProps: GetStaticProps<PageProps> = async ({ locale }) => {
  // freestyle isn't available in the UK
  if (locale === 'en-GB') {
    return {
      notFound: true,
    };
  }

  const copy = await getCopy();

  return {
    props: {
      copy,
    },
  };
};

/**
 * This function takes in the ClientInfoQuery object and answers two
 * questions:
 *
 * 1. has the user completed onboarding?
 * 2. does the user have the correct Fab allocation?
 *
 * If the answer to both questions is yes the function returns true.
 */
const hasPermissions = (data?: GetClientInfoQuery) =>
  !G.isUndefined(data) &&
  G.isObject(data.client) &&
  data.client.capabilities.canShop;

const useOnSearch = (setQueryParam: UseQueryParamReturn['setQueryParam']) => {
  useEffect(() => {
    const window = getWindow();

    // init the `stitchfix` object if need be
    if (G.isObject(window) && G.isUndefined(window?.stitchfix)) {
      window.stitchfix = {};
    }

    if (window?.stitchfix) {
      window.stitchfix.onSearch = (query: string) => setQueryParam('q', query);
    }
  }, [setQueryParam]);
};

const getSearchQuery = () => {
  const window = getWindow();

  const params = new URLSearchParams(window?.location?.search);

  return params.get('q');
};

/**
 * getContentDescription synchronously update the meta tag content description with the search
 * query parameter. If we do not have a search query parameter, it will fallback to a
 * generic description.
 */
const getContentDescription = (prefix: string) => {
  const searchQuery = getSearchQuery();

  if (G.isString(searchQuery)) {
    return `${prefix} “${searchQuery}”. Free shipping, returns & exchanges`;
  }

  return COPY.fallbackDescription;
};

/**
 * The `SearchPage` component handles top level concerns:
 *
 * 1. redirects to `/login` if client is not authenticated
 * 2. redirects to `/` if client does not have feature flag enabled
 * 3. redirects to `/` if search is not present in query parameters
 */
const SearchPage: NextPage<PageProps> = ({ copy }) => {
  const [isMissingPermissions, setIsMissingPermissions] = useState(false);
  const [isMissingQuery, setIsMissingQuery] = useState(false);
  const shouldForceRedirect = isMissingQuery || isMissingPermissions;
  const { queryParams, isReady, setQueryParam } = useSearchParams();
  const { q } = queryParams;

  const { EventProvider } = useScreenViewEvent({
    schema: 'screen_view',
    name: 'search_results',
    source_app: 'categories-next-ui',
  });

  useEffect(() => {
    if (isReady) setIsMissingQuery(G.isUndefined(q) || S.isEmpty(q));
  }, [isReady, q]);

  const { data: clientInfoData } = useGetClientInfoQuery({
    onCompleted: d => setIsMissingPermissions(!hasPermissions(d)),
  });

  const canRenderPageContent = q && hasPermissions(clientInfoData);

  useOnSearch(setQueryParam);

  useRedirect({
    shouldRedirect: shouldForceRedirect,
    path: '/',
  });

  return (
    <EventProvider>
      <Layout responsiveSection={{ width: 'xxlarge' }}>
        <Head>
          <title>{COPY.pageTitle}</title>
          <meta
            name="description"
            content={getContentDescription(copy.resultsForPrefix)}
          />
        </Head>

        <CopyProvider copy={copy}>
          {canRenderPageContent ? (
            <ResultsStateMachineProvider>
              <ToastProvider>
                <Stack spacing={{ sm: 1.5, md: 3 }} maxWidth={79.5} mx="auto">
                  <ResultsLayout />
                  <FixPromotion version="SEARCH" />
                </Stack>
              </ToastProvider>
            </ResultsStateMachineProvider>
          ) : (
            <PagePlaceholder />
          )}
        </CopyProvider>
      </Layout>
    </EventProvider>
  );
};

export default SearchPage;
